import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';
import styles from '../../css/modal.module.scss';
import { ReactComponent as ExitIcon } from '../../media/icon-exit.svg';

type PortalProps = {
  children: React.ReactNode;
};

const WRAPPER_ID = 'modal-root';
export const modalRoot = document.getElementById(WRAPPER_ID);

function Portal(props: PortalProps): JSX.Element {
  return ReactDOM.createPortal(props.children, modalRoot!);
}

type ModalProps = {
  isOpen: boolean;
  children: JSX.Element;
  onClose?: () => void;
  classNames?: string;
  blurredBackdrop?: boolean;
};

const PORTAL_CSS_CLASS_NAMES = {
  FADE_IN_BACKDROP: 'fade-in-backdrop',
  SLIDE_UP_MODAL: 'slide-up-modal',
  MODAL_IS_OPEN: 'modal-is-open',
};

export const Modal = React.memo(
  ({
    isOpen,
    children,
    onClose,
    classNames,
    blurredBackdrop,
  }: ModalProps): null | JSX.Element => {
    const backdropRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (isOpen && backdropRef?.current) {
        backdropRef.current.classList.add(
          PORTAL_CSS_CLASS_NAMES.FADE_IN_BACKDROP
        );
      }
      if (isOpen && modalRef?.current) {
        modalRef.current.focus();
        modalRef.current.classList.add(PORTAL_CSS_CLASS_NAMES.SLIDE_UP_MODAL);
      }
    }, [isOpen, modalRef.current, backdropRef.current]);

    return isOpen ? (
      <Portal>
        <div
          className={clsx(styles.backdrop, {
            [styles.blurred]: blurredBackdrop,
          })}
          ref={backdropRef}
        >
          <div className={clsx(styles.modal, classNames)} ref={modalRef}>
            {onClose && (
              <button onClick={onClose} className={styles.closeButton}>
                <ExitIcon />
              </button>
            )}
            {children}
          </div>
        </div>
      </Portal>
    ) : null;
  }
);

export function useModalToggle(initialState: boolean = false): {
  isOn: boolean;
  toggle: (state?: boolean) => void;
} {
  const [isOn, setIsOn] = useState<boolean>(initialState);

  const toggle = (state?: boolean): void => {
    if (state === true || state === false) {
      setIsOn(state);
    } else {
      setIsOn(!isOn);
    }
  };

  useEffect(() => {
    if (isOn) {
      document.body.classList.add(PORTAL_CSS_CLASS_NAMES.MODAL_IS_OPEN);
    } else {
      document.body.classList.remove(PORTAL_CSS_CLASS_NAMES.MODAL_IS_OPEN);
    }
  }, [isOn]);

  return {
    isOn,
    toggle,
  };
}
